/**
 * From → To range picker in ONE popover, with the round-trip-booking
 * interaction (same as CloudWatch Logs Insights' absolute tab): the first day
 * you click is the start, the next click is the end, and the days between
 * preview as you hover. Times are filled after, in the footer. When
 * `allowPresent` is set, the end may be left open ("Present") so the series
 * runs to the live edge.
 *
 * Values are the same literal wall-clock strings DateTimePicker emits
 * (`YYYY-MM-DDTHH:mm`) — zone-agnostic, interpreted as UTC by the analysis
 * range and the indicator anchors. `to === undefined` means "present".
 *
 * The range is only pushed up once it's complete (end picked, or Present), so
 * a half-made selection never reaches the chart.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { nowDateParts } from "@/utils/chartTime";
import { useAnchoredPopover } from "./useAnchoredPopover";
import { TimeFields } from "./TimeFields";
import {
  addMonths,
  dateKey,
  dayKey,
  display,
  format,
  monthCells,
  MONTHS_LONG,
  parse,
  WEEKDAYS,
  type Parts,
} from "./dateParts";
import base from "./DateTimePicker.module.css";
import styles from "./DateRangePicker.module.css";

interface Props {
  /** Start instant, `YYYY-MM-DDTHH:mm`. */
  from: string;
  /** End instant; `undefined` / `""` = the present (only if `allowPresent`). */
  to?: string;
  onChange: (from: string, to: string | undefined) => void;
  /** Allow an open end — renders the Present chip. */
  allowPresent?: boolean;
  /** Reference zone for "today" / the open-on month. See DateTimePicker. */
  zone?: "local" | "utc";
  /** Applied to the trigger button so the host controls its look. */
  className?: string;
  ariaLabel?: string;
}

const POPOVER_W = 292;
/** Default clock for a freshly picked end day (start defaults to 00:00). */
const END_DAY_TIME = { hour: 23, minute: 59 };

type Phase = "start" | "end";

interface Draft {
  from: Parts;
  /** null while the end is being picked, or when `present`. */
  to: Parts | null;
  present: boolean;
}

const withDay = (p: Parts, d: Date): Parts => ({
  ...p,
  year: d.getFullYear(),
  month: d.getMonth(),
  day: d.getDate(),
});

export function DateRangePicker({
  from,
  to,
  onChange,
  allowPresent = false,
  zone = "local",
  className,
  ariaLabel,
}: Props) {
  const [open, setOpen] = useState(false);
  const today = nowDateParts(zone);
  const todayParts: Parts = {
    year: today.y,
    month: today.m,
    day: today.d,
    hour: 0,
    minute: 0,
  };

  const seed = (): Draft => {
    const f = parse(from) ?? todayParts;
    const t = to ? parse(to) : null;
    return { from: f, to: t, present: !t && allowPresent };
  };

  const [draft, setDraft] = useState<Draft>(seed);
  const [phase, setPhase] = useState<Phase>("start");
  const [hover, setHover] = useState<Date | null>(null);
  const [view, setView] = useState(() => {
    const f = parse(from);
    return f ? { y: f.year, m: f.month } : { y: today.y, m: today.m };
  });

  const { triggerRef, popRef, coords } = useAnchoredPopover(
    open,
    () => setOpen(false),
    POPOVER_W,
    view,
  );

  // Reseed from props each time the popover opens: the draft is scratch state,
  // the props are the truth (they may have changed via Reset / scope switch).
  useEffect(() => {
    if (!open) return;
    const d = seed();
    setDraft(d);
    setPhase("start");
    setHover(null);
    setView({ y: d.from.year, m: d.from.month });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Remember the last end clock so re-picking end days keeps the user's time
  // instead of snapping back to 23:59 each click.
  const endTimeRef = useRef<{ hour: number; minute: number }>(END_DAY_TIME);
  useEffect(() => {
    const t = to ? parse(to) : null;
    if (t) endTimeRef.current = { hour: t.hour, minute: t.minute };
  }, [to]);

  /** Push up only a complete range — a pending start stays local. */
  const commit = (d: Draft) => {
    if (d.present) onChange(format(d.from, "datetime"), undefined);
    else if (d.to) onChange(format(d.from, "datetime"), format(d.to, "datetime"));
  };

  const update = (d: Draft) => {
    setDraft(d);
    commit(d);
  };

  const pickDay = (day: Date) => {
    // Starting a new range, or the click landed before the pending start —
    // either way this day becomes the start and we wait for the end.
    if (phase === "start" || dateKey(day) < dayKey(draft.from)) {
      setDraft((d) => ({ from: withDay(d.from, day), to: null, present: false }));
      setPhase("end");
      return;
    }
    const end: Parts = { ...withDay(draft.from, day), ...endTimeRef.current };
    setPhase("start");
    update({ ...draft, to: end, present: false });
  };

  const setFromTime = (hour: number, minute: number) => {
    const next = { ...draft, from: { ...draft.from, hour: clampH(hour), minute: clampM(minute) } };
    setDraft(next);
    commit(next);
  };

  const setToTime = (hour: number, minute: number) => {
    if (!draft.to) return;
    const t = { ...draft.to, hour: clampH(hour), minute: clampM(minute) };
    endTimeRef.current = { hour: t.hour, minute: t.minute };
    update({ ...draft, to: t });
  };

  const setPresent = () => {
    setPhase("start");
    update({ ...draft, to: null, present: true });
  };

  // Range shading: the committed/pending start through the end — or, while
  // picking, through the day under the cursor. "Present" shades to today.
  const rangeEndKey = (): number | null => {
    if (phase === "end") return hover ? dateKey(hover) : null;
    if (draft.present) return dayKey(todayParts);
    return draft.to ? dayKey(draft.to) : null;
  };
  const startKey = dayKey(draft.from);
  const endKey = rangeEndKey();

  const pending = phase === "end";
  const label = `${display(parse(from), "datetime", "Start")} → ${
    to ? display(parse(to), "datetime", "End") : allowPresent ? "Present" : "End"
  }`;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={className}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        {label}
      </button>
      {open && coords
        ? createPortal(
            <div
              ref={popRef}
              className={base.popover}
              role="dialog"
              style={{
                position: "fixed",
                top: coords.top,
                left: coords.left,
                width: POPOVER_W,
              }}
            >
              <div className={base.head}>
                <button
                  type="button"
                  className={base.nav}
                  onClick={() => setView((v) => addMonths(v, -1))}
                  aria-label="Previous month"
                >
                  ‹
                </button>
                <div className={base.monthLabel}>
                  {MONTHS_LONG[view.m]} {view.y}
                </div>
                <button
                  type="button"
                  className={base.nav}
                  onClick={() => setView((v) => addMonths(v, 1))}
                  aria-label="Next month"
                >
                  ›
                </button>
              </div>

              <div className={base.weekRow}>
                {WEEKDAYS.map((w) => (
                  <span key={w} className={base.weekday}>
                    {w}
                  </span>
                ))}
              </div>

              <div className={base.grid} onMouseLeave={() => setHover(null)}>
                {monthCells(view).map((d, i) => {
                  const k = dateKey(d);
                  const isStart = k === startKey;
                  const isEnd = endKey != null && k === endKey && !isStart;
                  const inRange =
                    endKey != null && k > Math.min(startKey, endKey) && k < Math.max(startKey, endKey);
                  return (
                    <button
                      key={i}
                      type="button"
                      className={[
                        base.day,
                        d.getMonth() === view.m ? "" : base.dayOut,
                        isStart || isEnd ? base.daySel : "",
                        inRange ? styles.inRange : "",
                        k === dayKey(todayParts) && !isStart && !isEnd
                          ? base.dayToday
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onMouseEnter={() => setHover(d)}
                      onClick={() => pickDay(d)}
                    >
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>

              <div className={styles.hint}>
                {pending
                  ? "Pick the end date"
                  : "Click a day to start a new range"}
              </div>

              <div className={styles.timeRow}>
                <span className={styles.timeLabel}>From</span>
                <TimeFields
                  compact
                  hour={draft.from.hour}
                  minute={draft.from.minute}
                  onChange={setFromTime}
                  ariaPrefix="Start"
                />
                <span className={styles.timeLabel}>To</span>
                {draft.present ? (
                  <span className={styles.presentTag}>Present</span>
                ) : draft.to ? (
                  <TimeFields
                    compact
                    hour={draft.to.hour}
                    minute={draft.to.minute}
                    onChange={setToTime}
                    ariaPrefix="End"
                  />
                ) : (
                  <span className={styles.pendingTag}>--:--</span>
                )}
              </div>

              <div className={styles.footer}>
                {allowPresent ? (
                  <button
                    type="button"
                    className={`${styles.presentBtn} ${
                      draft.present ? styles.presentBtnOn : ""
                    }`}
                    onClick={setPresent}
                    title="Run the end of the range to the present"
                  >
                    Present
                  </button>
                ) : null}
                <button
                  type="button"
                  className={base.doneBtn}
                  disabled={pending}
                  onClick={() => setOpen(false)}
                >
                  Done
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

const clampH = (h: number) => Math.max(0, Math.min(23, h || 0));
const clampM = (m: number) => Math.max(0, Math.min(59, m || 0));
